/**
 * RAG / Documents API client
 */
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const BASE = `${API_BASE_URL}/api/v1/docs`;

export interface Document {
  doc_id: string;
  title: string;
  source: string;
  tags: string[];
  char_count: number;
  chunk_count: number;
  created_at: string;
  metadata?: Record<string, any>;
}

export interface DocsStats {
  documents: number;
  chunks: number;
  total_chars: number;
  index_path: string;
}

export interface IngestRequest {
  title: string;
  text: string;
  tags?: string[];
  source?: string;
  metadata?: Record<string, any>;
}

export interface SearchResult {
  chunk_id: string;
  doc_id: string;
  title: string;
  tags: string[];
  chunk_index: number;
  text: string;
  score: number;
}

export interface SearchResponse {
  query: string;
  count: number;
  results: SearchResult[];
}

export interface NewsItem {
  title: string;
  url: string;
  source: string;
  published: string | null;
  snippet: string;
}

export interface NewsResponse {
  query: string;
  days: number;
  count: number;
  results: NewsItem[];
}

export const docsApi = {
  list: () => axios.get<{ documents: Document[]; stats: DocsStats }>(BASE).then(r => r.data),
  stats: () => axios.get<DocsStats>(`${BASE}/stats`).then(r => r.data),
  ingest: (req: IngestRequest) =>
    axios.post<{ ok: boolean; document: Document }>(`${BASE}/ingest`, req).then(r => r.data),
  ingestUrl: (url: string, tags?: string[], title_override?: string) =>
    axios.post<{
      ok: boolean;
      fetched: { url: string; final_url: string; title: string; domain: string; char_count: number };
      document: Document;
    }>(`${BASE}/ingest_url`, { url, tags, title_override }, { timeout: 30_000 })
      .then(r => r.data),
  upload: (file: File, title?: string, tags?: string) => {
    const fd = new FormData();
    fd.append('file', file);
    if (title) fd.append('title', title);
    if (tags) fd.append('tags', tags);
    return axios.post<{ ok: boolean; document: Document }>(`${BASE}/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  search: (query: string, top_k = 5, tags?: string[]) =>
    axios.post<SearchResponse>(`${BASE}/search`, { query, top_k, tags }).then(r => r.data),
  searchNews: (query: string, days = 7, max_items = 8) =>
    axios.post<NewsResponse>(`${BASE}/news`, { query, days, max_items },
      { timeout: 20_000 }).then(r => r.data),
  getChunk: (chunk_id: string) =>
    axios.get<ChunkDetail>(`${BASE}/chunk/${encodeURIComponent(chunk_id)}`).then(r => r.data),
  remove: (doc_id: string) =>
    axios.delete(`${BASE}/${doc_id}`).then(r => r.data),
};

export interface ChunkDetail {
  chunk_id: string;
  doc_id: string;
  chunk_index: number;
  text: string;
  document: { title: string; tags: string[]; source: string };
}
